import React from 'react';
import { Badge, ButtonV2, Icon, IconWithTextList, Spacer, Text } from '@breville/xps-react/dist';
import CookingIllustration from '../../assets/cooking-illustration.svg';
import { appConfig } from '../../library/utils/appConfig';
import { useLoginRedirect } from '../../hooks/useLoginRedirect';
import useUser from '../../hooks/useUser';
import '../scss/studioPassGate.scss';

export type StudioPassGateVariant = 'anonymous' | 'member';

const ACCENT_COLOR = '#FF5F42';

const BENEFITS = [
  { title: 'Unlimited cooking help, any time you need it' },
  { title: 'Recipe ideas personalized to your taste and pantry' },
  { title: 'Step-by-step guidance while you cook' },
  { title: 'First access to new recipes and techniques' },
];

const COPY: Record<StudioPassGateVariant, { title: string; subtitle: string; cta: string }> = {
  anonymous: {
    title: 'Meet Your AI Kitchen Assistant',
    subtitle: 'Get expert cooking help on demand — included with StudioPass VIP.',
    cta: 'Join StudioPass VIP',
  },
  member: {
    title: 'Cook Smarter with Your AI Assistant',
    subtitle:
      'The AI Assistant is a StudioPass VIP perk. Upgrade for personalized guidance every time you cook.',
    cta: 'Upgrade to VIP',
  },
};

export default function StudioPassGate({ variant }: { variant: StudioPassGateVariant }) {
  const { handleLoginClick } = useLoginRedirect();
  const { user } = useUser();
  const { title, subtitle, cta } = COPY[variant];

  return (
    <div className="studio-pass-gate">
      <div className="studio-pass-gate__illustration">
        <CookingIllustration />
      </div>

      <Spacer size="sm" />

      <Badge
        className="studio-pass-gate__badge"
        backgroundColor="rgba(255, 95, 66, 0.12)"
        fontColor={ACCENT_COLOR}
        borderColor="transparent"
      >
        <Icon iconPrefix="fak" iconName="star-solid" iconColor={ACCENT_COLOR} iconSize="1x" />
        <Text type="caps-label" tag="span">
          StudioPass VIP
        </Text>
      </Badge>

      <Spacer size="sm" />

      <Text type="h3" tag="h2" className="studio-pass-gate__title">
        {title}
      </Text>

      <Spacer size="xs" />

      <Text type="p2" tag="p" className="studio-pass-gate__subtitle">
        {subtitle}
      </Text>

      <Spacer size="md" />

      <IconWithTextList
        className="studio-pass-gate__benefits"
        columns={1}
        defaultIcon={{ iconPrefix: 'fak', iconName: 'circle-check-solid', iconColor: ACCENT_COLOR }}
        defaultTextProps={{ type: 'p2', tag: 'span' }}
        items={BENEFITS}
      />

      <Spacer size="md" />

      <ButtonV2
        className="studio-pass-gate__cta"
        text={cta}
        href={appConfig.studioPassUrl}
        variant="accent"
        fill="filled"
        shape="pill"
        size="fullWidth"
        icon={{ iconPrefix: 'fak', iconName: 'star-solid' }}
        alignIcon="left"
      />

      <Spacer size="xs" />

      {variant === 'anonymous' ? (
        <ButtonV2
          className="studio-pass-gate__secondary"
          text="Already a member? Sign in"
          onClick={handleLoginClick}
          variant="primary"
          fill="borderless"
          shape="pill"
          size="fullWidth"
        />
      ) : (
        user?.email && (
          <Text
            type="p4"
            tag="p"
            className="studio-pass-gate__secondary studio-pass-gate__secondary--static"
          >
            Signed in as {user.email}
          </Text>
        )
      )}
    </div>
  );
}
